import { useEffect, useMemo, useReducer, useState } from 'react';
import { useUtils } from '../../../../context/UtilsContext';
import { gutFibreCache, serializeGutFibreKey } from '../../../../utils/listCache/caches';
import { fetchDailyDietTrackingDetails, fetchPresetNutrition } from '../../../diet/core/api/dietApi';
import { useTemplateEventTypeId } from '../../../diet/core/eventTypes';
import { useProfileEventTypeId } from '../../../diet/workflow/plan/eventTypes';
import { monthSpan } from '../model/dates';
import { fibreDaysOf, fibreTargetOf } from '../model/fibre';

// Shared across hook instances so StrictMode double effects and remounts
// issue one request per key.
const inFlight = new Map();

function fetchOnce(key, fetcher) {
  if (!inFlight.has(key)) {
    const p = fetcher()
      .then((data) => gutFibreCache.set(key, data))
      .finally(() => inFlight.delete(key));
    inFlight.set(key, p);
  }
  return inFlight.get(key);
}

/**
 * The client's fibre target and diet tracking for the given months, one
 * request per month (a year at once takes ~20 s). Months already fetched today come from the cache. A failed
 * request marks its month as an error and never throws; remounting the tab
 * retries it.
 *
 * @param {string} userId - The client's UserProfile id.
 * @param {string[]} months - `YYYY-MM`, the months on screen.
 * @param {{ today: string, historyStart: string }} window - Days outside it are never requested.
 * @returns {{ target: number|null|undefined, months: Record<string, 'ready'|'loading'|'error'>, days: Array, unavailable: boolean }}
 *   `target` is undefined while loading; `unavailable` when the session has no diet event type.
 */
export function useGutFibre(userId, months, { today, historyStart }) {
  const { sessionUtils, utilsLoading } = useUtils() || {};
  // Same event types the Nutrition tab sends: the profile one for the target,
  // and for tracking the profile one, falling back to the template one.
  const presetEventTypeId = useProfileEventTypeId() || null;
  const templateEventTypeId = useTemplateEventTypeId() || null;
  const trackingEventTypeId = presetEventTypeId || templateEventTypeId;
  const utilsReady = Boolean(sessionUtils) || !utilsLoading;
  const enabled = Boolean(userId && today) && utilsReady;

  const [, bump] = useReducer((n) => n + 1, 0);
  const [failed, setFailed] = useState({});
  useEffect(() => gutFibreCache.subscribe(bump), []);

  const keyOf = (eventTypeId, part) => serializeGutFibreKey(userId, today, eventTypeId, part);
  const monthKey = months.join(',');

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const fail = (part) => live && setFailed((f) => ({ ...f, [part]: true }));
    if (presetEventTypeId) {
      const key = keyOf(presetEventTypeId, 'target');
      // Never rejects: on failure it resolves to zeros, which reads as no target.
      if (!gutFibreCache.get(key)) fetchOnce(key, () => fetchPresetNutrition(presetEventTypeId, userId).then(fibreTargetOf).then((target) => ({ target })));
    }
    if (trackingEventTypeId) {
      months.forEach((ym) => {
        const key = keyOf(trackingEventTypeId, ym);
        if (gutFibreCache.get(key)) return;
        const span = monthSpan(`${ym}-01`);
        const start = span.start < historyStart ? historyStart : span.start;
        const end = span.end > today ? today : span.end;
        if (start > end) return;
        fetchOnce(key, () => fetchDailyDietTrackingDetails(trackingEventTypeId, start, end, userId).then(fibreDaysOf)).catch(() => fail(ym));
      });
    }
    return () => {
      live = false;
    };
    // keyOf and months are covered by userId/today and monthKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId, today, historyStart, presetEventTypeId, trackingEventTypeId, monthKey]);

  const targetEntry = presetEventTypeId ? gutFibreCache.peek(keyOf(presetEventTypeId, 'target')) : null;
  const entries = months.map((ym) => [ym, trackingEventTypeId ? gutFibreCache.peek(keyOf(trackingEventTypeId, ym)) : null]);
  const unavailable = utilsReady && !trackingEventTypeId;
  const target = targetEntry ? targetEntry.data.target : utilsReady && !presetEventTypeId ? null : undefined;
  // Changes only when an entry lands or a month fails, so the result keeps
  // its identity across renders and the tab's memoised view isn't rebuilt.
  const signature = [
    target,
    unavailable,
    ...entries.map(([ym, e]) => `${ym}:${e ? e.ts : failed[ym] ? 'error' : 'loading'}`),
  ].join('|');

  return useMemo(() => {
    const collect = (list, failKey) => {
      const status = {};
      const days = [];
      list.forEach(([ym, e]) => {
        if (e) {
          status[ym] = 'ready';
          days.push(...e.data);
        } else {
          status[ym] = failed[failKey(ym)] ? 'error' : 'loading';
        }
      });
      return { status, days };
    };
    const fibre = collect(entries, (ym) => ym);
    return {
      target,
      months: fibre.status,
      days: fibre.days,
      unavailable,
    };
    // `signature` captures every input read here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}
